import React from 'react';
import LegalDocLayout from '../../components/ChinaDolphins/LegalDocLayout/LegalDocLayout';

const TOC = [
  { id: 'dispute-initiation', label: '1. Dispute Initiation & Evidence Submission' },
  { id: 'agent-review', label: '2. Sourcing Agent Review (72h SLA)' },
  { id: 'admin-arbitration', label: '3. ChinaDolphins Admin Arbitration' },
  { id: 'financial-resolution', label: '4. Dispute Resolution & Settlement Direction' },
  { id: 'failed-aggregation', label: '5. Failed Order Aggregation Pools & Refunds' },
];

export const RefundDisputePolicyContent = () => (
  <div>
    <p>
      This Refund and Dispute Resolution Policy defines the formal procedures for submitting, investigating, and resolving product quality claims, damaged or incorrect shipments, and failed order pooling scenarios on <strong>ChinaDolphins</strong>.
    </p>

    <h2 id="dispute-initiation">1. Dispute Initiation &amp; Evidence Submission</h2>
    <p>
      After an applicable order reaches the verified delivery stage, the Buyer may initiate a quality or non-conformity dispute directly within their Buyer Dashboard.
    </p>
    <p>
      To substantiate a claim, the Buyer must provide relevant supporting documentation, including:
    </p>
    <ul>
      <li>High-resolution photographs of defective units and packaging labels.</li>
      <li>Unboxing or warehouse receiving video footage demonstrating condition upon arrival.</li>
      <li>Clear technical description of discrepancies relative to approved specifications or samples.</li>
      <li>Independent third-party inspection report (where available or requested for high-value claims).</li>
    </ul>
    <p>
      The Buyer may formally request a <strong>partial refund</strong>, <strong>full refund</strong>, or <strong>re-production/replacement</strong> credit.
    </p>

    <h2 id="agent-review">2. Sourcing Agent Review (72-Hour SLA)</h2>
    <p>
      Upon submission, the dispute dossier is automatically dispatched to the assigned verified Sourcing Agent for immediate review.
    </p>
    <ul>
      <li>The Sourcing Agent is bound by a strict <strong>72-hour Service Level Agreement (SLA)</strong> to respond.</li>
      <li>The Agent may:
        <ol>
          <li><strong>Accept the claim:</strong> Authorizing the requested compensation.</li>
          <li><strong>Propose a mutual settlement:</strong> Offering an alternative partial credit or repair allowance.</li>
          <li><strong>Contest the claim:</strong> Submitting pre-shipment QC reports, factory testing logs, or proof of proper dispatch.</li>
        </ol>
      </li>
    </ul>

    <h2 id="admin-arbitration">3. ChinaDolphins Admin Arbitration</h2>
    <p>
      If the Buyer and Sourcing Agent are unable to reach a mutually satisfactory resolution within the initial response window, the dispute is escalated to the <strong>ChinaDolphins Compliance &amp; Arbitration Panel</strong>.
    </p>
    <p>
      The Arbitration Panel performs an objective audit of all available records, including:
    </p>
    <ul>
      <li>Buyer-submitted evidence and damage logs.</li>
      <li>Pre-shipment Quality Control documentation and factory sign-off sheets.</li>
      <li>On-platform messaging history and specification changes.</li>
      <li>Carrier shipping manifests, Bill of Lading, and customs inspection records.</li>
    </ul>
    <p>
      The decision rendered by ChinaDolphins Administration constitutes the platform&apos;s final internal determination.
    </p>

    <h2 id="financial-resolution">4. Dispute Resolution &amp; Settlement Direction</h2>
    <p>
      Depending on the arbitration outcome and the transaction milestone state, resolution may encompass:
    </p>
    <ul>
      <li>Direction to the approved third-party payment provider to initiate a partial or full refund to the Buyer&apos;s original payment method in accordance with provider rules.</li>
      <li>Adjustment of pending Agent commission allocations for future settlements.</li>
      <li>Withholding administrative milestone authorization for supplier disbursements under the applicable payment workflow.</li>
      <li>Account restrictions or blacklisting for habitual non-compliant suppliers.</li>
    </ul>
    <p>
      ChinaDolphins does not operate an escrow account or provide banking services; actual refund execution is performed by the designated third-party payment provider in accordance with its operating procedures.
    </p>

    <h2 id="failed-aggregation">5. Failed Order Aggregation Pools &amp; Refunds</h2>
    <p>
      A failed aggregation pool is distinct from a quality dispute:
    </p>
    <ul>
      <li>If an Order Aggregation cycle does not reach the required Factory Minimum Order Quantity (MOQ) prior to the published pool deadline, the aggregation cycle automatically closes without entering manufacturing.</li>
      <li>Any pre-authorized or collected Buyer funds are released or refunded in full through the approved payment provider according to applicable payment rules (ACH, SEPA, Wire) without platform penalty.</li>
    </ul>
  </div>
);

const RefundDisputePolicyPage = () => {
  return (
    <LegalDocLayout
      title="Refund & Dispute Resolution Policy"
      subtitle="Standardized procedure for quality disputes, inspections, agent reviews, and failed aggregation refunds."
      effectiveDate="January 1, 2026"
      lastUpdated="August 13, 2026"
      jurisdiction="Hong Kong (MTN GLOBAL LIMITED)"
      tocItems={TOC}
    >
      <RefundDisputePolicyContent />
    </LegalDocLayout>
  );
};

export default RefundDisputePolicyPage;
